import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { 
  X, MessageSquare, Loader2, Search, User, ChevronLeft, Gift, 
  ShoppingBag, CheckCircle, Send, Image as ImageIcon, 
  Banknote, Globe, BellOff, Video, Phone, Tag,
  ShoppingCart, Store, Plus, Wallet, Lock, ShieldCheck,
  CreditCard as CardIcon, Landmark, Trash2, MapPin, AlertCircle, Flag, Download,
  Eye, FileText, Palette, Ruler, Package, ExternalLink, Clock
} from 'lucide-react';
import { db, auth } from '../../services/firebase'; 
import { 
  collection, query, where, onSnapshot, 
  doc, setDoc, addDoc, updateDoc, serverTimestamp, getDocs, limit, getDoc, increment 
} from 'firebase/firestore'; 
import { Chat, UserProfile } from '../../types'; 
import { useCart } from '../../contexts/CartContext';

// 🟢 DYNAMIC YOCO SDK HOOK
const useYoco = (publicKey: string | undefined) => {
  const [yoco, setYoco] = useState<any>(null);
  useEffect(() => {
    if (!publicKey) { setYoco(null); return; }
    if (window.YocoSDK) { setYoco(new window.YocoSDK({ publicKey })); return; }
    const script = document.createElement('script');
    script.src = "https://js.yoco.com/sdk/v1/yoco-sdk-web.js";
    script.async = true;
    script.onload = () => { if (window.YocoSDK) setYoco(new window.YocoSDK({ publicKey })); };
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, [publicKey]);
  return yoco;
};

// 🟢 PAYSTACK SDK HOOK
const usePaystack = () => {
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        if (window.PaystackPop) { setLoaded(true); return; }
        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v1/inline.js';
        script.async = true;
        script.onload = () => setLoaded(true);
        document.body.appendChild(script);
    }, []);
    return loaded;
};

declare global { interface Window { YocoSDK: any; PaystackPop: any; } }

// ============================================================================
// 🟢 DESIGN PREVIEW MODAL
// ============================================================================
const DesignPreviewModal = ({ isOpen, onClose, design }: any) => {
  if (!isOpen || !design) return null;

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-zinc-900 border-b border-white/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-orange-500" />
            <h3 className="text-white font-black uppercase tracking-widest text-sm">Design Details</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Design Image */}
          <div className="aspect-square bg-black rounded-xl overflow-hidden border border-white/10 max-h-[300px] mx-auto">
            <img src={design.image} className="w-full h-full object-contain" alt={design.name} />
          </div>

          {/* Design Info */}
          <div className="space-y-3">
            <h4 className="text-white font-black text-xl uppercase tracking-tight">{design.name}</h4>
            <p className="text-zinc-400 text-sm">{design.category || 'Design'}</p>
            
            {design.description && (
              <p className="text-zinc-300 text-sm border-t border-white/10 pt-3">{design.description}</p>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3">
            {design.colors && design.colors.length > 0 && (
              <div className="p-3 bg-zinc-800/50 rounded-xl border border-white/5">
                <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1">
                  <Palette size={12} /> Colors
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {design.colors.map((color: string) => (
                    <span key={color} className="px-2 py-0.5 bg-zinc-700 rounded-full text-[8px] text-white font-bold">
                      {color}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {design.sizes && design.sizes.length > 0 && (
              <div className="p-3 bg-zinc-800/50 rounded-xl border border-white/5">
                <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1">
                  <Ruler size={12} /> Sizes
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {design.sizes.map((size: string) => (
                    <span key={size} className="px-2 py-0.5 bg-zinc-700 rounded-full text-[8px] text-white font-bold">
                      {size}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {design.moq && (
              <div className="p-3 bg-zinc-800/50 rounded-xl border border-white/5">
                <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1">
                  <Package size={12} /> MOQ
                </p>
                <p className="text-white font-bold text-sm">{design.moq} units</p>
              </div>
            )}
            
            {design.leadTime && (
              <div className="p-3 bg-zinc-800/50 rounded-xl border border-white/5">
                <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1">
                  <Clock size={12} /> Lead Time
                </p>
                <p className="text-white font-bold text-sm">{design.leadTime}</p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex gap-3 pt-3 border-t border-white/10">
            <button 
              onClick={() => {
                // Open in DesignerGate or product view
                window.dispatchEvent(new CustomEvent('open-product', { 
                  detail: { productId: design.id } 
                }));
                onClose();
              }}
              className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
            >
              <ExternalLink size={14} /> View Product
            </button>
            <button 
              onClick={onClose}
              className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// 🟢 DESIGN ENQUIRY CARD COMPONENT
// ============================================================================
const DesignEnquiryCard = ({ msg, isMe, onViewDesign }: any) => {
  const design = msg.designProject;
  const details = msg.enquiryDetails;
  
  if (!design) return null;

  return (
    <div className={`max-w-[90%] sm:max-w-[85%] lg:max-w-[60%] rounded-2xl lg:rounded-none overflow-hidden border-2 flex flex-col ${
      isMe ? 'border-orange-500/50 bg-zinc-900' : 'border-zinc-700 bg-zinc-800'
    }`}>
      {/* Header */}
      <div className="p-3 sm:p-4 flex items-center gap-3 border-b border-inherit bg-black/30">
        <FileText size={14} className="text-orange-400" />
        <span className="text-[9px] font-bold text-orange-400 uppercase tracking-widest">Design Enquiry</span>
        {details?.status && (
          <span className={`ml-auto text-[8px] font-black uppercase px-2 py-0.5 rounded-full ${
            details.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
            details.status === 'viewed' ? 'bg-blue-500/20 text-blue-400' :
            details.status === 'replied' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
          }`}>
            {details.status}
          </span>
        )}
      </div>

      {/* Design Preview */}
      <div className="flex gap-4 p-4">
        <div className="w-24 h-24 rounded-lg bg-black overflow-hidden shrink-0 border border-white/10">
          <img src={design.image} className="w-full h-full object-cover" alt={design.name} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-bold text-sm truncate">{design.name}</h4>
          <p className="text-zinc-400 text-[10px] uppercase tracking-widest">{design.category || 'Design'}</p>
          
          {/* Enquiry Details */}
          <div className="flex flex-wrap gap-2 mt-2">
            {details?.quantity && (
              <span className="text-[8px] font-bold bg-zinc-700 px-2 py-0.5 rounded-full text-white">
                Qty: {details.quantity}
              </span>
            )}
            {details?.selectedColor && details.selectedColor !== 'N/A' && (
              <span className="text-[8px] font-bold bg-zinc-700 px-2 py-0.5 rounded-full text-white flex items-center gap-1">
                <Palette size={8} /> {details.selectedColor}
              </span>
            )}
            {details?.selectedSize && details.selectedSize !== 'N/A' && (
              <span className="text-[8px] font-bold bg-zinc-700 px-2 py-0.5 rounded-full text-white flex items-center gap-1">
                <Ruler size={8} /> {details.selectedSize}
              </span>
            )}
          </div>

          {/* Questions Preview */}
          {details?.questions && details.questions.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {details.questions.slice(0, 2).map((q: string, i: number) => (
                <p key={i} className="text-[8px] text-zinc-400 truncate">• {q}</p>
              ))}
              {details.questions.length > 2 && (
                <p className="text-[8px] text-zinc-500">+{details.questions.length - 2} more questions</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="p-3 border-t border-inherit flex gap-2 bg-black/20">
        <button 
          onClick={() => onViewDesign(design)}
          className="flex-1 py-2 bg-orange-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-orange-600 transition-colors flex items-center justify-center gap-1.5"
        >
          <Eye size={12} /> View Design
        </button>
        <button 
          className="flex-1 py-2 bg-white/10 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-white/20 transition-colors flex items-center justify-center gap-1.5"
          onClick={() => {
            // Open chat input with quick reply
            const input = document.querySelector('input[placeholder="Type message..."]') as HTMLInputElement;
            if (input) {
              input.value = "I've reviewed your design enquiry. Let me get back to you with a quote.";
              input.focus();
            }
          }}
        >
          <Send size={12} /> Quick Reply
        </button>
      </div>
    </div>
  );
};

// --- SWIPEABLE CHAT ITEM COMPONENT ---
const SwipeableChatItem = ({ chat, currentUserId, onClick, onDelete, isActive }: any) => {
    const [offset, setOffset] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const startX = useRef(0);
    const longPressTimer = useRef<any>(null);
    const isSwiping = useRef(false);

    const otherId = chat.participants?.find((id: string) => id !== currentUserId) || '';
    const name = chat.participantNames?.[otherId] || 'User';
    const avatar = chat.participantAvatars?.[otherId];
    const unreadCount = chat.unreadCount?.[currentUserId] || 0;

    const handleTouchStart = (e: React.TouchEvent) => {
        startX.current = e.touches[0].clientX;
        isSwiping.current = false;
        longPressTimer.current = setTimeout(() => {
            if (!isSwiping.current) {
                if (navigator.vibrate) navigator.vibrate(50);
                setIsMenuOpen(true);
            }
        }, 500);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        const currentX = e.touches[0].clientX;
        const diff = currentX - startX.current;
        if (Math.abs(diff) > 10) { clearTimeout(longPressTimer.current); isSwiping.current = true; }
        if (diff < 0 && diff > -100) setOffset(diff);
    };

    const handleTouchEnd = () => {
        clearTimeout(longPressTimer.current);
        if (offset < -50) setOffset(-80);
        else setOffset(0);
    };

    const reset = (e?: any) => { e?.stopPropagation(); setOffset(0); setIsMenuOpen(false); };

    const displayMessage = typeof chat.lastMessage === 'object' && chat.lastMessage !== null ? chat.lastMessage.text : chat.lastMessage;

    return (
        <div className="relative overflow-hidden mb-2 select-none touch-pan-y group">
            <div className="absolute inset-0 flex justify-end items-center bg-black pr-5 lg:hidden">
                <Trash2 className="text-zinc-500" size={20} />
            </div>

            <div 
                onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
                onClick={() => { if(offset === 0 && !isMenuOpen) onClick(); else reset(); }}
                className={`relative border border-zinc-800 p-5 flex items-center gap-4 transition-all duration-200 ease-out cursor-pointer lg:border-x-0 lg:border-t-0 lg:border-b rounded-2xl lg:rounded-none
                    ${isMenuOpen ? 'scale-[0.98] brightness-50' : 'active:bg-zinc-900'} 
                    ${isActive ? 'bg-zinc-900 border-l-4 border-l-white' : 'bg-black hover:bg-zinc-950'}
                `}
                style={{ transform: `translateX(${offset}px)` }}
            >
                <div className="w-12 h-12 rounded-full lg:rounded-none bg-zinc-900 overflow-hidden shrink-0 border border-zinc-800 flex items-center justify-center text-zinc-400 font-bold text-xl uppercase">
                    {avatar ? <img src={avatar} className="w-full h-full object-cover grayscale" alt={name}/> : name.charAt(0)}
                </div>
                
                <div className="flex-1 min-w-0 pr-6">
                    <div className="flex justify-between items-baseline mb-1">
                        <h4 className={`text-sm truncate uppercase tracking-widest ${unreadCount > 0 ? 'font-black text-white' : 'font-bold text-zinc-300'}`}>{name}</h4>
                        <span className="text-[9px] text-zinc-500 font-mono tracking-widest">{chat.updatedAt?.toDate ? chat.updatedAt.toDate().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ''}</span>
                    </div>
                    <p className={`text-xs truncate font-mono ${unreadCount > 0 ? 'text-white font-bold' : 'text-zinc-500'}`}>{displayMessage}</p>
                </div>
                
                {unreadCount > 0 && (
                    <div className="min-w-[20px] h-5 px-1.5 rounded-full lg:rounded-none bg-white flex items-center justify-center absolute right-5 border border-black">
                        <span className="text-[10px] font-black text-black">{unreadCount}</span>
                    </div>
                )}

                <button onClick={(e) => { e.stopPropagation(); onDelete(chat.id); }} className="hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-white transition-all">
                    <Trash2 size={16} />
                </button>
            </div>

            {isMenuOpen && (
                <div className="absolute inset-0 z-20 flex items-center justify-center gap-4 animate-in fade-in zoom-in duration-200 lg:hidden bg-black/80 backdrop-blur-sm rounded-2xl" onClick={reset}>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(chat.id); }} className="w-14 h-14 bg-black rounded-full flex items-center justify-center text-white border-2 border-white"><Trash2 size={20} /></button>
                    <button onClick={reset} className="w-14 h-14 bg-white text-black rounded-full flex items-center justify-center border-2 border-black"><X size={20} /></button>
                </div>
            )}
        </div>
    );
};

// --- MAIN COMPONENT ---
interface MessagesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile | null;
  onViewProfile?: (userId: string) => void;
  onViewBrand?: (brandId: string) => void;
  onSelectProduct?: (product: any) => void;
  initialChatUserId?: string | null;
  initialMessageText?: string; 
  initialGiftAction?: any;
}

export const MessagesPanel: React.FC<MessagesPanelProps> = ({
  isOpen, onClose, currentUser, onViewProfile, onViewBrand, onSelectProduct, initialChatUserId, initialMessageText, initialGiftAction
}) => {
  const { addToCart } = useCart();
  const navigate = useNavigate(); 
  const paystackLoaded = usePaystack();

  const [chats, setChats] = useState<Chat[]>([]);
  const [activeChat, setActiveChat] = useState<Chat | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [users, setUsers] = useState<any[]>([]);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportStatus, setReportStatus] = useState<'idle' | 'reporting' | 'success'>('idle');

  const [stickerBase64, setStickerBase64] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [giftMode, setGiftMode] = useState<'send' | 'request' | 'share' | null>(null);
  const [giftType, setGiftType] = useState<'product' | 'cash'>('product'); 
  const [giftAmount, setGiftAmount] = useState('');
  const [pendingActionData, setPendingActionData] = useState<any | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [confirmingProduct, setConfirmingProduct] = useState<any | null>(null);
  
  const [activeBrandSettings, setActiveBrandSettings] = useState<any | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState<'yoco' | 'paystack' | ''>('');

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showAddressStep, setShowAddressStep] = useState(false);
  const [includeAddress, setIncludeAddress] = useState(false);
  const [senderAddressInput, setSenderAddressInput] = useState({ name: '', line1: '', city: '', phone: '' });

  const [claimingGiftId, setClaimingGiftId] = useState<string | null>(null);
  const [claimingMessageId, setClaimingMessageId] = useState<string | null>(null);
  const [receiverAddressInput, setReceiverAddressInput] = useState({ name: '', line1: '', city: '', phone: '' });

  const [messages, setMessages] = useState<any[]>([]);
  const [inputText, setInputText] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const currentUserId = currentUser?.uid;

  // 🟢 DESIGN PREVIEW STATE
  const [selectedDesign, setSelectedDesign] = useState<any>(null);
  const [showDesignPreview, setShowDesignPreview] = useState(false);

  const yoco = useYoco(activeBrandSettings?.yoco?.enabled ? activeBrandSettings.yoco.publicKey : undefined);
  const autoSentMarker = useRef(false);

  useEffect(() => {
      if (isOpen) document.body.style.overflow = 'hidden';
      else {
          document.body.style.overflow = 'unset';
          autoSentMarker.current = false;
      }
      return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // 🟢 INITIALIZE THE CHAT
  useEffect(() => {
      if (isOpen && initialChatUserId && currentUserId) {
          const initChat = async () => {
              const userDoc = await getDoc(doc(db, 'users', initialChatUserId));
              if (userDoc.exists()) { 
                  startChat({ id: userDoc.id, ...userDoc.data() }, true); 
              }
          };
          initChat();
      }
  }, [isOpen, initialChatUserId, currentUserId]);

  // 🟢 AUTO-SEND ENGINE
  useEffect(() => {
      if (isOpen && initialMessageText && activeChat && !autoSentMarker.current) {
          autoSentMarker.current = true;
          
          const fireAutoSend = async () => {
              const otherId = activeChat.participants?.find((id: string) => id !== currentUserId);
              if (!otherId || !currentUserId) return;

              try {
                  await addDoc(collection(db, "chats", activeChat.id, "messages"), {
                      text: initialMessageText, senderId: currentUserId, createdAt: serverTimestamp(), messageType: 'text'
                  });
                  await setDoc(doc(db, "chats", activeChat.id), { 
                      lastMessage: initialMessageText, 
                      updatedAt: serverTimestamp(), 
                      hidden: { [currentUserId]: false, [otherId]: false }, 
                      [`unreadCount.${otherId}`]: increment(1) 
                  }, { merge: true });
              } catch (e) { console.error("Auto-send error:", e); }
          };
          
          fireAutoSend();
      }
  }, [isOpen, initialMessageText, activeChat, currentUserId]);

  useEffect(() => {
      if (isOpen && initialGiftAction) {
          if (initialChatUserId) {
              setIsSearching(false);
              if (initialGiftAction.type === 'send') setConfirmingProduct(initialGiftAction.data);
          } else {
              setPendingActionData(initialGiftAction.data);
              if (initialGiftAction.type === 'send') setGiftMode('send');
              else if (initialGiftAction.type === 'share_product' || initialGiftAction.type === 'request') setGiftMode('request');
              else setGiftMode('share');
              setIsSearching(true);
          }
      }
  }, [isOpen, initialGiftAction, initialChatUserId]);

  // FETCH BRAND SETTINGS
  useEffect(() => {
    const fetchBrandForPayment = async () => {
        const product = confirmingProduct || pendingActionData;
        const brandId = product?.businessId || product?.brandId;
        if (!brandId || brandId === 'unknown') return;

        setSettingsLoading(true);
        try {
            const snap = await getDoc(doc(db, "users", brandId));
            if (snap.exists()) setActiveBrandSettings(snap.data());
        } catch (e) { console.error("Gift settings fetch error:", e); }
        finally { setSettingsLoading(false); }
    };

    if (confirmingProduct || (giftMode === 'send' && pendingActionData)) {
        fetchBrandForPayment();
    }
  }, [confirmingProduct, pendingActionData, giftMode]);

  useEffect(() => {
    if (!isOpen || !currentUserId) return;
    const q = query(collection(db, "chats"), where("participants", "array-contains", currentUserId));
    const unsub = onSnapshot(q, (snap) => {
      let chatList = snap.docs.map(d => ({ id: d.id, ...d.data() } as Chat));
      // @ts-ignore
      chatList = chatList.filter(c => !c.hidden?.[currentUserId]);
      setChats(chatList.sort((a: any, b: any) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
    });
    return () => unsub();
  }, [isOpen, currentUserId]);

  useEffect(() => {
    if (!activeChat) return;
    if (currentUserId) {
        updateDoc(doc(db, "chats", activeChat.id), { [`unreadCount.${currentUserId}`]: 0 }).catch(console.error);
    }
    const q = query(collection(db, "chats", activeChat.id, "messages"));
    const unsub = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMessages(msgs.sort((a: any, b: any) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    });
    return () => unsub();
  }, [activeChat]);

  useEffect(() => {
    if (giftMode && !pendingActionData) {
        const fetchProducts = async () => {
            const snap = await getDocs(query(collection(db, "products"), limit(20)));
            setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchProducts();
    }
  }, [giftMode, pendingActionData]);

  useEffect(() => {
    if (isSearching) {
      const fetchUsers = async () => {
        const snap = await getDocs(query(collection(db, "users"), limit(50)));
        const filtered = snap.docs.map(d => {
            const data = d.data() as any;
            return { id: d.id, ...data, photoURL: data.avatar || data.photoURL };
        }).filter((u: any) => u.id !== currentUserId && (!userSearchTerm || u.displayName?.toLowerCase().includes(userSearchTerm.toLowerCase())));
        setUsers(filtered);
      };
      fetchUsers();
    }
  }, [isSearching, userSearchTerm, currentUserId]);

  const handleDeleteChat = async (chatId: string) => {
      if (!window.confirm("Permanently archive this channel?")) return;
      try { 
          await updateDoc(doc(db, "chats", chatId), { [`hidden.${currentUserId}`]: true }); 
          if(activeChat?.id === chatId) setActiveChat(null); 
      } catch (error) {}
  };

  const handleDownloadImage = async (url: string) => {
      try {
          const response = await fetch(url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `scoop_asset_${Date.now()}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
          console.error('Download failed:', error);
          window.open(url, '_blank');
      }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          const reader = new FileReader();
          reader.onloadend = () => setStickerBase64(reader.result as string);
          reader.readAsDataURL(file);
      }
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if ((!inputText.trim() && !stickerBase64) || !activeChat || !activeChat.id || !currentUserId) return;
    
    const text = inputText;
    const sticker = stickerBase64;
    
    setInputText(""); 
    setStickerBase64(null);

    const otherId = activeChat.participants?.find((id: string) => id !== currentUserId);
    if (!otherId) return;

    const messagePayload: any = {
        text,
        senderId: currentUserId,
        createdAt: serverTimestamp(),
        messageType: sticker ? 'image' : 'text'
    };
    if (sticker) messagePayload.imageUrl = sticker;

    try {
        await addDoc(collection(db, "chats", activeChat.id, "messages"), messagePayload);
        await setDoc(doc(db, "chats", activeChat.id), { 
            lastMessage: sticker ? 'Sent an image' : text, 
            updatedAt: serverTimestamp(), 
            hidden: { [currentUserId]: false, [otherId]: false }, 
            [`unreadCount.${otherId}`]: increment(1) 
        }, { merge: true });
    } catch (error: any) {
        console.error("Message Error:", error);
    }
  };

  const handleRequestProduct = async (product: any) => {
    if (!activeChat || !currentUserId) return;
    const otherId = activeChat.participants.find((id: string) => id !== currentUserId) || '';
    
    await addDoc(collection(db, "chats", activeChat.id, "messages"), { 
        text: `Requested an asset`, 
        senderId: currentUserId, 
        createdAt: serverTimestamp(), 
        product, 
        messageType: 'item_request' 
    });
    
    await setDoc(doc(db, "chats", activeChat.id), { 
        lastMessage: `Requested ${product.name}`, 
        updatedAt: serverTimestamp(), 
        [`unreadCount.${otherId}`]: increment(1) 
    }, { merge: true });
    
    setGiftMode(null); 
    setPendingActionData(null);
  };

  const handleRequestCash = async () => {
      if (!activeChat || !currentUserId || !giftAmount) return;
      const otherId = activeChat.participants.find((id: string) => id !== currentUserId) || '';
      await addDoc(collection(db, "chats", activeChat.id, "messages"), { text: `System Request: Credit Transfer`, amount: parseFloat(giftAmount), senderId: currentUserId, createdAt: serverTimestamp(), messageType: 'cash_request' });
      await setDoc(doc(db, "chats", activeChat.id), { lastMessage: `Requested ${giftAmount} CRD`, updatedAt: serverTimestamp(), [`unreadCount.${otherId}`]: increment(1) }, { merge: true });
      setGiftMode(null); setGiftAmount('');
  };

  const handleReserveProduct = async () => {
      if (!confirmingProduct || !activeChat || !currentUserId) return;
      const otherId = activeChat.participants.find((id: string) => id !== currentUserId) || 'unknown';
      setIsProcessingPayment(true);
      
      addToCart({
          ...confirmingProduct,
          quantity: 1,
          selectedSize: confirmingProduct.selectedSize || 'One Size',
          selectedColor: confirmingProduct.selectedColor || 'Standard',
          forUser: otherId,
          isGift: true
      });

      await addDoc(collection(db, "chats", activeChat.id, "messages"), { text: `Asset reserved in cart for acquisition.`, senderId: currentUserId, createdAt: serverTimestamp(), product: confirmingProduct, messageType: 'gift_pending', expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) });
      await setDoc(doc(db, "chats", activeChat.id), { lastMessage: `Reserved asset`, updatedAt: serverTimestamp(), [`unreadCount.${otherId}`]: increment(1) }, { merge: true });
      
      setPaymentSuccess(true);
      setTimeout(() => { setConfirmingProduct(null); setGiftMode(null); setPaymentSuccess(false); setIsProcessingPayment(false); }, 1500);
  };

  const handleSendFromWallet = async () => {
      const amount = parseFloat(giftAmount);
      // @ts-ignore
      const userBal = currentUser?.cred || currentUser?.credits || 0;
      if (amount > userBal) { alert("Insufficient wallet balance."); return; }
      if (!activeChat || !currentUserId) return;

      setIsProcessingPayment(true);
      try {
          const otherId = activeChat.participants.find((id) => id !== currentUserId) || 'unknown';
          
          await updateDoc(doc(db, "users", currentUserId), {
              cred: increment(-amount),
              credits: increment(-amount)
          });
          
          await updateDoc(doc(db, "users", otherId), {
              cred: increment(amount),
              credits: increment(amount)
          });

          await addDoc(collection(db, "chats", activeChat.id, "messages"), { text: `Transferred ${amount} CRD from Wallet.`, amount, senderId: currentUserId, createdAt: serverTimestamp(), messageType: 'cash_sent' });
          await setDoc(doc(db, "chats", activeChat.id), { lastMessage: `Transferred ${amount} CRD`, updatedAt: serverTimestamp(), [`unreadCount.${otherId}`]: increment(1) }, { merge: true });

          setPaymentSuccess(true);
          setTimeout(() => { setGiftAmount(''); setGiftMode(null); setPaymentSuccess(false); }, 1500);
      } catch (e) {
          console.error(e);
          alert("Transfer failed.");
      }
      setIsProcessingPayment(false);
  };

  const triggerPayment = () => {
      let amount = 0; 
      if (confirmingProduct) amount = confirmingProduct.price;
      else if (giftAmount) amount = parseFloat(giftAmount);
      if (amount <= 0) return alert("System Error: Invalid Amount");

      setIsProcessingPayment(true);

      if (paymentProvider === 'yoco') {
          if (!yoco) { setIsProcessingPayment(false); return alert("Brand Terminal Offline (Yoco)."); }
          yoco.showPopup({ 
              amountInCents: amount * 100, 
              currency: 'ZAR', 
              name: activeBrandSettings?.businessName || activeBrandSettings?.displayName || 'Gift Authorization', 
              description: confirmingProduct ? `Asset: ${confirmingProduct.name}` : `Credit Purchase` 
          }, 
          (result: any) => { 
              setIsProcessingPayment(false); 
              if (result.error) alert("Authorization Failed: " + result.error.message); 
              else handlePaymentSuccess(); 
          });
      } else if (paymentProvider === 'paystack') {
          if (!paystackLoaded || !activeBrandSettings?.paystack?.publicKey) { 
              setIsProcessingPayment(false); return alert("Brand Terminal Offline (Paystack)."); 
          }
          const handler = window.PaystackPop.setup({
              key: activeBrandSettings.paystack.publicKey, 
              email: currentUser?.email || 'guest@scoop.app', 
              amount: amount * 100, 
              currency: 'ZAR',
              callback: (response: any) => { 
                  setIsProcessingPayment(false); 
                  if (response.status === 'success') handlePaymentSuccess(); 
                  else alert("Authorization Failed"); 
              },
              onClose: () => { setIsProcessingPayment(false); }
          });
          handler.openIframe();
      }
  };

  const handlePaymentSuccess = () => {
      if (confirmingProduct) {
          setShowAddressStep(true);
      } else {
          finalizeGiftSend(false);
      }
  };

  const finalizeGiftSend = async (withAddress: boolean) => {
    if (!activeChat || !currentUserId) return;
    try {
        const otherId = activeChat.participants.find((id: string) => id !== currentUserId) || 'unknown';
        if (confirmingProduct) {
            const shippingData = withAddress ? senderAddressInput : { name: 'Pending', line1: 'Pending', city: '', phone: '' };
            const orderRef = await addDoc(collection(db, "orders"), { userId: currentUserId, businessId: confirmingProduct.businessId || 'admin', items: [{ productId: confirmingProduct.id, name: confirmingProduct.name, price: confirmingProduct.price, quantity: 1, image: confirmingProduct.images?.[0] || confirmingProduct.image }], total: confirmingProduct.price, status: 'paid', isGift: true, recipientId: otherId, needsAddress: !withAddress, shippingAddress: shippingData, createdAt: serverTimestamp() });
            await addDoc(collection(db, "chats", activeChat.id, "messages"), { text: withAddress ? `Asset Transferred. 📦` : `Asset Transferred. Location required. 📦`, senderId: currentUserId, createdAt: serverTimestamp(), product: confirmingProduct, orderId: orderRef.id, needsAddress: !withAddress, messageType: 'gift_paid' });
        } else if (giftAmount) {
            const amount = parseFloat(giftAmount);
            await updateDoc(doc(db, "users", otherId), {
                cred: increment(amount),
                credits: increment(amount)
            });
            await addDoc(collection(db, "chats", activeChat.id, "messages"), { text: `Transferred ${amount} CRD.`, amount, senderId: currentUserId, createdAt: serverTimestamp(), messageType: 'cash_sent' });
        }
        await setDoc(doc(db, "chats", activeChat.id), { lastMessage: `Transferred Asset`, updatedAt: serverTimestamp(), [`unreadCount.${otherId}`]: increment(1) }, { merge: true });
        setPaymentSuccess(true);
        setTimeout(() => { setConfirmingProduct(null); setGiftMode(null); setGiftAmount(''); setPaymentSuccess(false); setIsProcessingPayment(false); setShowAddressStep(false); }, 2500);
    } catch (error) { setIsProcessingPayment(false); }
  };

  const handleClaimGift = async () => {
      if (!claimingGiftId || !claimingMessageId || !activeChat) return;
      try { await updateDoc(doc(db, "orders", claimingGiftId), { shippingAddress: receiverAddressInput, needsAddress: false }); await updateDoc(doc(db, "chats", activeChat.id, "messages", claimingMessageId), { needsAddress: false, text: "Logistics confirmed! 🚚" }); alert("Location Secured."); setClaimingGiftId(null); } catch (error) {}
  };

  const startChat = async (targetUser: any, isDirectJump = false) => {
    if (!currentUserId) return;
    const chatId = [currentUserId, targetUser.id].sort().join("_");
    
    const chatData = { 
        participants: [currentUserId, targetUser.id], 
        updatedAt: serverTimestamp(), 
        participantNames: { 
            [currentUserId]: currentUser?.displayName || "Me", 
            [targetUser.id]: targetUser.displayName || "User" 
        }, 
        participantAvatars: { 
            [currentUserId]: currentUser?.avatar || currentUser?.photoURL || "", 
            [targetUser.id]: targetUser.avatar || targetUser.photoURL || "" 
        }, 
        hidden: { 
            [currentUserId]: false, 
            [targetUser.id]: false 
        } 
    };
    
    await setDoc(doc(db, "chats", chatId), chatData, { merge: true });
    setActiveChat({ id: chatId, ...chatData } as Chat);
    setIsSearching(false);

    if (pendingActionData && !isDirectJump) {
        if (giftMode === 'share') {
             const isBrand = pendingActionData.isBrand || pendingActionData.role === 'brand';
             await addDoc(collection(db, "chats", chatId, "messages"), { 
                 text: `Shared a profile`, 
                 senderId: currentUserId, 
                 createdAt: serverTimestamp(), 
                 messageType: 'profile_share', 
                 profile: { 
                     id: pendingActionData.id || pendingActionData.uid, 
                     displayName: pendingActionData.displayName || pendingActionData.name, 
                     photoURL: pendingActionData.photoURL || pendingActionData.logo || pendingActionData.logoUrl,
                     isBrand: isBrand
                 } 
             });
             await setDoc(doc(db, "chats", chatId), { lastMessage: `Shared a profile`, updatedAt: serverTimestamp(), [`unreadCount.${targetUser.id}`]: increment(1) }, { merge: true });
             setGiftMode(null); 
        }
        if (giftMode === 'send') setConfirmingProduct(pendingActionData);
        if (giftMode === 'request') handleRequestProduct(pendingActionData);
        setPendingActionData(null);
    }
  };

  // 🟢 HANDLE VIEW DESIGN
  const handleViewDesign = (design: any) => {
    setSelectedDesign(design);
    setShowDesignPreview(true);
  };

  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()));
  
  if (!isOpen) return null;

  // ============================================================================
  // UI BLOCKS
  // ============================================================================

  const InboxUI = (
      <div className="flex flex-col h-full bg-black w-full relative border-r border-zinc-800">
          <div className="p-6 lg:p-10 border-b border-zinc-800 flex justify-between items-center bg-black sticky top-0 z-20">
            <div className="flex items-center gap-4">
                {isSearching && <button onClick={() => { setIsSearching(false); setUserSearchTerm(''); setPendingActionData(null); }} className="w-10 h-10 flex items-center justify-center bg-zinc-900 border border-zinc-700 text-white hover:bg-white hover:text-black transition-colors rounded-full lg:rounded-none shrink-0"><ChevronLeft size={20}/></button>}
                <h2 className="text-4xl lg:text-5xl font-black text-white uppercase italic tracking-tighter leading-none">{isSearching ? (pendingActionData ? 'Transmit To' : 'Network') : 'Messages'}</h2>
            </div>
            <button onClick={onClose} className="lg:hidden w-10 h-10 flex items-center justify-center bg-zinc-900 border border-zinc-700 text-white hover:bg-white hover:text-black transition-colors rounded-full shrink-0"><X size={20} /></button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 lg:p-8 space-y-2 bg-black">
            {isSearching ? (
                 <div className="space-y-8">
                    <div className="flex items-center gap-4 border-b-2 border-white pb-4 mb-4">
                        <Search className="text-zinc-500" size={20} />
                        <input autoFocus placeholder="Search personnel..." className="w-full bg-transparent text-xl font-black uppercase tracking-widest text-white outline-none focus:border-white transition-colors placeholder:text-zinc-700" value={userSearchTerm} onChange={(e) => setUserSearchTerm(e.target.value)}/>
                    </div>
                    <div className="space-y-3">
                        {users.map(u => (
                            <button key={u.id} onClick={() => { startChat(u); }} className="w-full flex items-center gap-4 p-5 border-2 border-zinc-900 hover:border-white transition-all text-left group bg-zinc-950 rounded-2xl lg:rounded-none">
                                <div className="w-12 h-12 rounded-full lg:rounded-none bg-black border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0 text-zinc-400 font-bold text-xl uppercase">
                                    {u.photoURL ? <img src={u.photoURL} className="w-full h-full object-cover grayscale group-hover:grayscale-0" alt={u.displayName}/> : u.displayName?.charAt(0) || 'U'}
                                </div>
                                <div>
                                    <h4 className="font-black text-white uppercase tracking-widest text-sm mb-1 leading-none">{u.displayName}</h4>
                                    <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-widest group-hover:text-zinc-400">{u.username ? `@${u.username}` : 'Network Member'}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                 </div>
            ) : (
                chats.length === 0 ? 
                    <div className="text-center py-32 flex flex-col items-center justify-center opacity-30">
                        <MessageSquare size={64} strokeWidth={1.5} className="text-white mb-6"/>
                        <p className="text-white font-mono uppercase tracking-[0.4em] text-xs">Silence on all channels</p>
                    </div> : 
                    chats.map(chat => <SwipeableChatItem key={chat.id} chat={chat} currentUserId={currentUserId} onClick={() => setActiveChat(chat)} onDelete={handleDeleteChat} isActive={activeChat?.id === chat.id} />)
            )}
          </div>
          {!isSearching && 
            <div className="p-6 lg:p-8 border-t border-zinc-800 bg-black">
                <button onClick={() => setIsSearching(true)} className="w-full py-5 rounded-full lg:rounded-none border border-white text-white font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-white hover:text-black transition-colors shadow-2xl"><Plus size={16} /> New Chat</button>
            </div>
          }
      </div>
  );

  const ClaimUI = claimingGiftId && (
        <div className="flex flex-col h-full bg-zinc-950 p-6 lg:p-12 animate-in slide-in-from-right duration-300 relative w-full">
            <div className="flex items-center mb-12 border-b border-zinc-800 pb-6">
                <button onClick={() => setClaimingGiftId(null)} className="w-10 h-10 flex items-center justify-center bg-zinc-900 border border-zinc-700 text-white hover:bg-white hover:text-black transition-colors mr-6 rounded-full lg:rounded-none shrink-0"><ChevronLeft size={20}/></button>
                <h2 className="text-4xl font-black text-white italic uppercase tracking-tighter">Delivery Details</h2>
            </div>
            <div className="space-y-6 max-w-xl">
                <input className="w-full bg-black border-2 border-zinc-800 rounded-2xl lg:rounded-none p-5 text-white font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" placeholder="ATTN: FULL NAME" value={receiverAddressInput.name} onChange={e => setReceiverAddressInput({...receiverAddressInput, name: e.target.value})} />
                <input className="w-full bg-black border-2 border-zinc-800 rounded-2xl lg:rounded-none p-5 text-white font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" placeholder="STREET ADDRESS" value={receiverAddressInput.line1} onChange={e => setReceiverAddressInput({...receiverAddressInput, line1: e.target.value})} />
                <div className="flex gap-4">
                    <input className="w-1/2 bg-black border-2 border-zinc-800 rounded-2xl lg:rounded-none p-5 text-white font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" placeholder="CITY" value={receiverAddressInput.city} onChange={e => setReceiverAddressInput({...receiverAddressInput, city: e.target.value})} />
                    <input className="w-1/2 bg-black border-2 border-zinc-800 rounded-2xl lg:rounded-none p-5 text-white font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" placeholder="CONTACT NO." value={receiverAddressInput.phone} onChange={e => setReceiverAddressInput({...receiverAddressInput, phone: e.target.value})} />
                </div>
                <button onClick={handleClaimGift} className="w-full py-6 border border-white bg-white text-black font-black rounded-full lg:rounded-none uppercase tracking-[0.2em] text-xs mt-8 hover:bg-zinc-200 shadow-2xl transition-colors">Confirm Logistics</button>
            </div>
        </div>
  );

  const PaymentUI = (confirmingProduct || (giftType === 'cash' && giftAmount && !giftMode)) && (
        <div className="flex flex-col h-full bg-black animate-in slide-in-from-right duration-300 relative w-full">
            <style dangerouslySetInnerHTML={{__html: `.yoco-container, .yoco-frame-container, #yoco-container, iframe[src*="yoco"] { z-index: 2147483647 !important; position: fixed !important; } iframe[name^="paystack"] { z-index: 2147483647 !important; }`}} />
            <div className="p-6 lg:p-10 border-b border-zinc-800 flex items-center gap-4 bg-black sticky top-0 z-10">
                <button onClick={() => { setConfirmingProduct(null); setGiftAmount(''); setShowAddressStep(false); }} className="w-10 h-10 flex items-center justify-center bg-zinc-900 border border-zinc-700 text-white hover:bg-white hover:text-black transition-colors rounded-full lg:rounded-none shrink-0"><ChevronLeft size={20} /></button>
                <h2 className="text-3xl lg:text-4xl font-black text-white uppercase italic tracking-tighter">Terminal</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6 lg:p-12 space-y-12">
                {paymentSuccess ? (
                    <div className="text-center animate-in zoom-in py-24 flex flex-col items-center h-full justify-center">
                        <CheckCircle className="w-32 h-32 text-white mx-auto mb-8" strokeWidth={1.5} />
                        <h2 className="text-6xl font-black text-white mb-2 uppercase italic tracking-tighter">Authorized</h2>
                        <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.4em] mt-4">Transaction complete</p>
                    </div>
                ) : !showAddressStep ? (
                    <div className="max-w-xl mx-auto space-y-12">
                        <div className="border border-zinc-800 bg-zinc-950/50 p-8 rounded-2xl lg:rounded-none">
                            <div className="flex items-center gap-6 mb-8">
                                <div className="h-24 w-20 bg-black border border-zinc-800 rounded-lg lg:rounded-none overflow-hidden flex items-center justify-center shrink-0">
                                    {!confirmingProduct ? <Banknote size={32} className="text-white opacity-50"/> : <img src={confirmingProduct.images?.[0] || confirmingProduct.image} className="w-full h-full object-cover grayscale"/>}
                                </div>
                                <div>
                                    <p className="text-white font-black uppercase tracking-widest text-lg leading-tight mb-2">{!confirmingProduct ? "Credit Transfer" : confirmingProduct.name}</p>
                                    <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-[0.3em]">Gift Asset</p>
                                </div>
                            </div>
                            <div className="flex justify-between items-end text-white font-black border-t border-dashed border-zinc-700 pt-6">
                                <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Total Due</span>
                                <span className="text-3xl leading-none">{!confirmingProduct ? giftAmount + ' CRD' : 'ZAR ' + confirmingProduct.price}</span>
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.3em] mb-4 flex items-center gap-3 border-b border-zinc-800 pb-2"><Wallet size={14} /> Authorization Method</label>
                            
                            {!activeBrandSettings?.yoco?.enabled && !activeBrandSettings?.paystack?.enabled ? (
                                <div className="p-4 border-2 border-dashed border-red-900 text-red-500 font-mono text-xs flex items-center gap-3 uppercase tracking-widest bg-red-950/20 rounded-xl mt-6">
                                    <AlertCircle size={20}/> Terminal Offline: Brand has not configured payment gateways.
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4 mt-6">
                                    {activeBrandSettings?.yoco?.enabled && (
                                        <button onClick={() => setPaymentProvider('yoco')} className={`h-32 rounded-2xl lg:rounded-none border-2 flex flex-col items-center justify-center gap-4 transition-all ${paymentProvider === 'yoco' ? 'bg-white border-white text-black shadow-lg lg:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.2)]' : 'bg-black border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'}`}><CardIcon size={28} strokeWidth={1.5} /><span className="font-black text-[10px] uppercase tracking-widest">Yoco Card</span></button>
                                    )}
                                    {activeBrandSettings?.paystack?.enabled && (
                                        <button onClick={() => setPaymentProvider('paystack')} className={`h-32 rounded-2xl lg:rounded-none border-2 flex flex-col items-center justify-center gap-4 transition-all ${paymentProvider === 'paystack' ? 'bg-white border-white text-black shadow-lg lg:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.2)]' : 'bg-black border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'}`}><Landmark size={28} strokeWidth={1.5} /><span className="font-black text-[10px] uppercase tracking-widest">Paystack</span></button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col gap-4 mt-12 border-t border-zinc-800 pt-12">
                            <button onClick={triggerPayment} disabled={isProcessingPayment || !paymentProvider} className="w-full py-6 bg-white rounded-full lg:rounded-none border border-white text-black font-black uppercase tracking-[0.2em] text-xs hover:bg-zinc-200 transition-colors flex items-center justify-center gap-3 shadow-xl disabled:opacity-50">
                                {isProcessingPayment ? <Loader2 className="animate-spin" size={18} /> : <>Authorize {!confirmingProduct ? giftAmount + ' CRD' : 'ZAR ' + confirmingProduct.price} <Lock size={14}/></>}
                            </button>
                            {confirmingProduct && <button onClick={handleReserveProduct} disabled={isProcessingPayment} className="w-full py-6 bg-black rounded-full lg:rounded-none text-white font-black uppercase tracking-[0.2em] text-xs border border-zinc-800 hover:border-white hover:bg-white hover:text-black transition-colors disabled:opacity-50">Reserve & Notify</button>}
                        </div>
                    </div>
                ) : (
                    <div className="animate-in fade-in slide-in-from-right space-y-12 max-w-xl mx-auto">
                        <div className="text-left border-b border-zinc-800 pb-6">
                            <h3 className="text-white font-black text-4xl uppercase italic tracking-tighter mb-4">Logistics</h3>
                            <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-[0.3em]">Configure destination data now?</p>
                        </div>
                        {!includeAddress ? (
                            <div className="grid grid-cols-2 gap-4">
                                <button onClick={() => setIncludeAddress(true)} className="w-full py-8 border-2 border-white rounded-2xl lg:rounded-none bg-black text-white font-black uppercase tracking-widest text-[10px] hover:bg-white hover:text-black transition-colors flex flex-col items-center justify-center gap-3"><MapPin size={20}/> Input Location</button>
                                <button onClick={() => finalizeGiftSend(false)} className="w-full py-8 border-2 border-zinc-800 rounded-2xl lg:rounded-none bg-zinc-950 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:bg-zinc-800 hover:text-white transition-colors flex flex-col items-center justify-center gap-3"><Send size={20} /> Let them enter it</button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <input placeholder="ATTN: FULL NAME" className="w-full bg-black border-2 border-zinc-800 rounded-2xl lg:rounded-none p-5 text-white font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" onChange={e => setSenderAddressInput({...senderAddressInput, name: e.target.value})} />
                                <input placeholder="STREET ADDRESS" className="w-full bg-black border-2 border-zinc-800 rounded-2xl lg:rounded-none p-5 text-white font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" onChange={e => setSenderAddressInput({...senderAddressInput, line1: e.target.value})} />
                                <div className="flex gap-4">
                                    <input placeholder="CITY" className="w-1/2 bg-black border-2 border-zinc-800 rounded-2xl lg:rounded-none p-5 text-white font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" onChange={e => setSenderAddressInput({...senderAddressInput, city: e.target.value})} />
                                    <input placeholder="CONTACT NO." className="w-1/2 bg-black border-2 border-zinc-800 rounded-2xl lg:rounded-none p-5 text-white font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" onChange={e => setSenderAddressInput({...senderAddressInput, phone: e.target.value})} />
                                </div>
                                <button onClick={() => finalizeGiftSend(true)} className="w-full py-6 border border-white bg-white text-black font-black uppercase tracking-[0.2em] text-xs mt-8 hover:bg-zinc-200 transition-colors shadow-2xl rounded-full lg:rounded-none">Confirm & Dispatch</button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
  );

  const ShopUI = (giftMode && activeChat && giftMode !== 'share') && (
        <div className="flex flex-col h-full bg-black animate-in slide-in-from-right duration-300 relative w-full">
            <div className="p-6 lg:p-10 border-b border-zinc-800 flex items-center gap-4 bg-black sticky top-0 z-10">
                <button onClick={() => setGiftMode(null)} className="w-10 h-10 flex items-center justify-center bg-zinc-900 border border-zinc-700 text-white hover:bg-white hover:text-black transition-colors rounded-full lg:rounded-none shrink-0"><ChevronLeft size={20} /></button>
                <h2 className="text-3xl lg:text-4xl font-black text-white italic uppercase tracking-tighter leading-none">
                    {giftMode === 'send' ? 'Select Asset' : 'Request Asset'}
                </h2>
            </div>
            
            <div className="p-6 lg:p-10 pb-0 flex gap-4">
                <button onClick={() => setGiftType('product')} className={`flex-1 py-3 sm:py-4 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.3em] rounded-l-full lg:rounded-none border-2 ${giftType === 'product' ? 'bg-white text-black border-white' : 'bg-black border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'}`}>Database</button>
                <button onClick={() => setGiftType('cash')} className={`flex-1 py-3 sm:py-4 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.3em] rounded-r-full lg:rounded-none border-2 border-l-0 lg:border-l-2 ${giftType === 'cash' ? 'bg-white text-black border-white' : 'bg-black border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'}`}>Credits</button>
            </div>

            {giftType === 'cash' ? (
                <div className="flex-1 p-8 flex flex-col items-center justify-center space-y-6 max-w-xl mx-auto w-full">
                    <Banknote size={80} className="text-zinc-800" strokeWidth={1}/>
                    <h3 className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tighter italic text-center">
                        {giftMode === 'send' ? 'Transfer' : 'Request'}
                    </h3>
                    <div className="relative w-full border-b-4 border-zinc-800 focus-within:border-white transition-colors pb-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 font-black text-2xl">CRD</span>
                        <input type="number" placeholder="0" className="w-full bg-transparent p-4 pl-20 text-white text-5xl font-black outline-none transition-colors text-center tracking-tighter" value={giftAmount} onChange={(e) => setGiftAmount(e.target.value)} />
                    </div>
                    
                    {giftMode === 'send' ? (
                        <div className="w-full space-y-3 mt-6">
                            <button disabled={!giftAmount || isProcessingPayment} onClick={handleSendFromWallet} className="w-full py-4 sm:py-5 rounded-2xl lg:rounded-none border border-white bg-white text-black font-black uppercase tracking-widest text-[10px] sm:text-xs disabled:opacity-30 hover:bg-zinc-200 transition-colors shadow-2xl flex items-center justify-center gap-2">
                               {isProcessingPayment ? <Loader2 className="animate-spin" size={16}/> : <><Wallet size={16}/> Send from Wallet (Bal: {(currentUser as any)?.cred || (currentUser as any)?.credits || 0})</>}
                            </button>
                            <button disabled={!giftAmount || isProcessingPayment} onClick={() => setGiftMode(null)} className="w-full py-4 sm:py-5 rounded-2xl lg:rounded-none border border-zinc-800 bg-zinc-950 text-white font-black uppercase tracking-widest text-[10px] sm:text-xs disabled:opacity-30 hover:border-white transition-colors flex items-center justify-center gap-2">
                                <CardIcon size={16}/> Buy Credits (Card)
                            </button>
                        </div>
                    ) : (
                        <button disabled={!giftAmount} onClick={handleRequestCash} className="w-full py-4 sm:py-6 rounded-2xl lg:rounded-none bg-white text-black font-black uppercase tracking-[0.2em] text-[10px] sm:text-xs disabled:opacity-30 hover:bg-zinc-200 transition-colors shadow-2xl mt-4 border border-white">
                            Transmit Request
                        </button>
                    )}
                </div>
            ) : (
                <div className="flex flex-col h-full overflow-hidden">
                    <div className="p-6 lg:p-10 pb-0 relative shrink-0">
                        <Search className="absolute left-12 lg:left-16 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                        <input placeholder="SEARCH ARCHIVES..." className="w-full bg-black border-2 border-zinc-800 rounded-full lg:rounded-none text-white pl-16 pr-4 py-4 sm:py-5 font-mono text-xs uppercase tracking-widest outline-none focus:border-white transition-colors placeholder:text-zinc-700" value={productSearch} onChange={(e) => setProductSearch(e.target.value)}/>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 lg:p-10 grid grid-cols-2 gap-4 sm:gap-6 pb-20">
                        {filteredProducts.map(product => (
                            <button key={product.id} onClick={() => giftMode === 'send' ? setConfirmingProduct(product) : handleRequestProduct(product)} className="group text-left flex flex-col gap-4 border border-zinc-900 bg-zinc-950 p-4 hover:border-zinc-700 transition-colors rounded-2xl lg:rounded-none">
                                <div className="aspect-[3/4] bg-black border border-zinc-800 relative overflow-hidden rounded-lg lg:rounded-none">
                                    <img src={product.images?.[0] || product.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={product.name}/>
                                    {giftMode === 'send' && <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-white text-black text-[9px] sm:text-[10px] font-black px-2 sm:px-3 py-1 rounded-full lg:rounded-none border border-black uppercase tracking-widest">ZAR {product.price}</div>}
                                </div>
                                <div>
                                    <h4 className="font-black text-white text-xs sm:text-sm uppercase tracking-widest truncate mb-1">{product.name}</h4>
                                    <p className="text-zinc-600 text-[9px] sm:text-[10px] uppercase font-bold group-hover:text-white transition-colors font-mono tracking-widest">{giftMode === 'send' ? 'Acquire & Send' : 'Request Asset'}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
  );

  const ChatUI = activeChat && (() => {
        const otherId = activeChat.participants.find((id: string) => id !== currentUserId) || '';
        const name = activeChat.participantNames?.[otherId] || 'User';
        const avatar = activeChat.participantAvatars?.[otherId];

        const sendAutoReply = async (text: string) => {
            if (!activeChat || !currentUserId) return;
            try {
                await addDoc(collection(db, "chats", activeChat.id, "messages"), {
                    text, senderId: currentUserId, createdAt: serverTimestamp(), messageType: 'text'
                });
                await setDoc(doc(db, "chats", activeChat.id), { 
                    lastMessage: text, updatedAt: serverTimestamp(), [`unreadCount.${otherId}`]: increment(1) 
                }, { merge: true });
            } catch (e) { console.error(e); }
        };

        return (
            <div className="flex flex-col h-full bg-zinc-950 animate-in fade-in duration-300 relative w-full">
                {/* Chat Header */}
                <div className="p-4 sm:p-6 lg:p-8 border-b border-zinc-800 flex items-center justify-between bg-black sticky top-0 z-20 shadow-xl">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <button onClick={() => setActiveChat(null)} className="w-10 h-10 flex items-center justify-center bg-zinc-900 border border-zinc-700 text-white hover:bg-white hover:text-black transition-colors lg:hidden rounded-full shrink-0"><ChevronLeft size={20} /></button>
                    <button onClick={() => {
                        if(onViewProfile) {
                            onClose();
                            onViewProfile(otherId);
                        }
                    }} className="flex items-center gap-3 sm:gap-5 group min-w-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-black rounded-full lg:rounded-none border border-zinc-700 flex items-center justify-center text-zinc-400 font-bold text-xl uppercase overflow-hidden shrink-0">
                            {avatar ? <img src={avatar} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" /> : name.charAt(0)}
                        </div>
                        <div className="text-left min-w-0">
                            <h3 className="text-white font-black uppercase tracking-widest text-base sm:text-lg leading-none mb-1 group-hover:underline underline-offset-4 truncate">{name}</h3>
                            <p className="text-zinc-600 text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.3em]">Inspect Profile</p>
                        </div>
                    </button>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => handleDeleteChat(activeChat.id)} className="lg:hidden w-10 h-10 flex items-center justify-center bg-black border border-zinc-800 text-zinc-500 hover:text-red-500 hover:border-red-500 transition-colors rounded-full shrink-0" title="Delete Chat">
                        <Trash2 size={18} />
                    </button>
                    <button onClick={() => {
                        setShowReportModal(true);
                        setReportStatus('idle');
                    }} className="w-10 h-10 flex items-center justify-center bg-black border border-zinc-800 text-zinc-500 hover:text-white transition-colors rounded-full shrink-0" title="Report Conversation">
                        <Flag size={18} />
                    </button>
                  </div>
                </div>

                {/* Message Log */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10 space-y-6 sm:space-y-8 bg-zinc-950">
                  {messages.map((msg) => {
                    const isMe = msg.senderId === currentUserId;
                    const isPaidGift = msg.messageType === 'gift_paid';
                    const isPendingGift = msg.messageType === 'gift_pending'; 
                    const isCashReq = msg.messageType === 'cash_request';
                    const isCashSent = msg.messageType === 'cash_sent';
                    const showClaimButton = isPaidGift && !isMe && msg.needsAddress && msg.orderId;
                    
                    const isProfileShare = msg.messageType === 'brand_share' || msg.messageType === 'profile_share';
                    const profileData = msg.profile || msg.brand || {}; 
                    const isBrandProfile = profileData.isBrand || msg.messageType === 'brand_share';
                    const profileId = profileData.id || profileData.uid;
                    const profileName = profileData.displayName || profileData.name || 'User';
                    const profilePhoto = profileData.photoURL || profileData.logo || profileData.logoUrl;

                    const isItemReq = msg.messageType === 'item_request';
                    const isItemShare = msg.messageType === 'item_share';
                    const isPostShare = msg.messageType === 'post_share'; 
                    const postData = msg.post || msg.sharedPost || {};
                    const isImageShare = msg.messageType === 'image' || msg.imageUrl;

                    // 🟢 DESIGN ENQUIRY CHECK
                    const isDesignEnquiry = msg.messageType === 'design_enquiry';

                    const textStr = msg.text || '';
                    const isOfferMessage = textStr.includes('made an offer') && textStr.includes('confirm?');
                    const isAvailabilityAsk = textStr.includes('still available');

                    return (
                      <div key={msg.id} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                        
                        {/* 🟢 DESIGN ENQUIRY CARD */}
                        {isDesignEnquiry && msg.designProject ? (
                          <DesignEnquiryCard 
                            msg={msg} 
                            isMe={isMe} 
                            onViewDesign={handleViewDesign} 
                          />
                        ) : 
                        
                        /* 🟢 OFFER CARD */
                        isOfferMessage ? (
                            <div className={`max-w-[90%] sm:max-w-[85%] lg:max-w-[60%] p-5 sm:p-6 rounded-2xl lg:rounded-none border-2 flex flex-col shadow-xl ${isMe ? 'border-zinc-700 bg-black' : 'border-cyan-900/50 bg-cyan-950/20'}`}>
                                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 text-cyan-500 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] border-b border-zinc-800 pb-3"><Tag size={14}/> Marketplace Offer</div>
                                <p className="text-sm sm:text-base font-medium text-white leading-relaxed mb-4">{msg.text}</p>
                                {!isMe && (
                                    <div className="flex gap-3">
                                        <button onClick={() => sendAutoReply("Offer Accepted! 🤝 Let's proceed with the transaction.")} className="flex-1 py-3 bg-white text-black text-[10px] font-black uppercase tracking-widest rounded-lg lg:rounded-none hover:bg-zinc-200 transition-colors">Accept</button>
                                        <button onClick={() => sendAutoReply("Offer Declined. 🙏 Please submit a new offer or purchase at list price.")} className="flex-1 py-3 bg-zinc-950 text-zinc-400 border border-zinc-800 text-[10px] font-black uppercase tracking-widest rounded-lg lg:rounded-none hover:text-white hover:border-zinc-600 transition-colors">Decline</button>
                                    </div>
                                )}
                                {isMe && (
                                    <div className="bg-zinc-900/50 border border-zinc-800 p-3 text-center text-zinc-500 text-[10px] uppercase tracking-widest font-bold rounded-lg lg:rounded-none">Awaiting Seller Response</div>
                                )}
                            </div>
                        ) : 
                        
                        /* 🟢 INQUIRY CARD */
                        isAvailabilityAsk ? (
                            <div className={`max-w-[90%] sm:max-w-[85%] lg:max-w-[60%] p-5 sm:p-6 rounded-2xl lg:rounded-none border-2 flex flex-col shadow-xl ${isMe ? 'border-zinc-700 bg-black' : 'border-zinc-800 bg-zinc-900'}`}>
                                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 text-zinc-400 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] border-b border-zinc-800 pb-3"><ShoppingBag size={14}/> Item Inquiry</div>
                                <p className="text-sm sm:text-base font-medium text-white leading-relaxed mb-4">{msg.text}</p>
                                {!isMe && (
                                    <div className="flex gap-3">
                                        <button onClick={() => sendAutoReply("Yes, it's still available! Let me know if you have any questions.")} className="flex-1 py-3 bg-white text-black text-[10px] font-black uppercase tracking-widest rounded-lg lg:rounded-none hover:bg-zinc-200 transition-colors">Yes, Available</button>
                                        <button onClick={() => sendAutoReply("Sorry, this item is no longer available.")} className="flex-1 py-3 bg-zinc-950 text-zinc-400 border border-zinc-800 text-[10px] font-black uppercase tracking-widest rounded-lg lg:rounded-none hover:text-white hover:border-zinc-600 transition-colors">Unavailable</button>
                                    </div>
                                )}
                            </div>
                        ) :

                        isProfileShare ? (
                            <div className={`max-w-[90%] sm:max-w-[85%] lg:max-w-[60%] border-2 p-5 sm:p-6 rounded-2xl lg:rounded-none flex flex-col gap-5 sm:gap-6 ${isMe ? 'border-zinc-700 bg-black' : 'border-zinc-800 bg-zinc-900'}`}>
                                <div className="flex items-center gap-4 sm:gap-5">
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-black rounded-full lg:rounded-none border border-zinc-700 overflow-hidden shrink-0 flex items-center justify-center text-zinc-400 font-bold text-2xl uppercase">
                                        {profilePhoto ? <img src={profilePhoto} className="w-full h-full object-cover grayscale"/> : profileName.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-white font-black uppercase tracking-widest mb-1 leading-none truncate">{profileName}</h3>
                                        <p className="text-zinc-500 text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.3em]">{isBrandProfile ? 'Brand Profile' : 'User Profile'}</p>
                                    </div>
                                </div>
                                <button onClick={() => { 
                                    onClose();
                                    if (!profileId) return; 
                                    if (isBrandProfile && onViewBrand) onViewBrand(profileId); 
                                    else if (onViewProfile) onViewProfile(profileId); 
                                }} className={`w-full py-3 sm:py-4 rounded-full lg:rounded-none border-2 font-black uppercase tracking-[0.2em] text-[9px] sm:text-[10px] transition-colors ${isMe ? 'bg-white text-black border-white hover:bg-zinc-200' : 'bg-black text-white border-zinc-700 hover:border-white'}`}>
                                    View Profile
                                </button>
                            </div>
                        ) : isPostShare ? (
                            <div className={`max-w-[90%] sm:max-w-[85%] lg:max-w-[60%] rounded-2xl lg:rounded-none overflow-hidden border-2 flex flex-col ${isMe ? 'border-zinc-700 bg-black' : 'border-zinc-800 bg-zinc-900'}`}>
                                <div className="p-3 sm:p-4 flex items-center gap-3 border-b-2 border-inherit">
                                    <div className="w-8 h-8 rounded-full lg:rounded-none bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs font-bold uppercase overflow-hidden shrink-0 text-zinc-500">
                                        {postData.authorImage || postData.userPhoto ? <img src={postData.authorImage || postData.userPhoto} className="w-full h-full object-cover grayscale" /> : (postData.authorName || postData.userName || 'U').charAt(0)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white font-black text-[10px] sm:text-xs uppercase tracking-widest leading-none truncate">{postData.authorName || postData.userName || 'Creator'}</p>
                                    </div>
                                </div>
                                
                                <div className="bg-black relative border-b-2 border-inherit aspect-[4/5] flex items-center justify-center overflow-hidden group">
                                    {(postData.videoUrl || postData.video) ? (
                                        <video src={postData.videoUrl || postData.video} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" autoPlay muted loop playsInline />
                                    ) : (
                                        <img src={postData.image || postData.imageUrl || postData.images?.[0]} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                    )}
                                </div>
                                
                                <div className="p-4 sm:p-5 flex flex-col">
                                    <p className="text-zinc-400 font-mono text-[9px] sm:text-[10px] uppercase tracking-widest line-clamp-2 mb-4 leading-relaxed">
                                        {postData.title || postData.caption || postData.description || 'Community Update'}
                                    </p>
                                    
                                    <button onClick={() => { 
                                        onClose(); 
                                        if (postData.authorId) navigate(`/profile/${postData.authorId}`);
                                        else navigate('/community');
                                    }} className={`w-full py-3 sm:py-4 rounded-full lg:rounded-none border-2 font-black uppercase tracking-[0.2em] text-[9px] sm:text-[10px] transition-colors ${isMe ? 'bg-white text-black border-white hover:bg-zinc-200' : 'bg-black text-white border-zinc-700 hover:border-white'}`}>
                                        Inspect Content
                                    </button>
                                </div>
                            </div>
                        ) : msg.product ? (
                            <div className={`max-w-[90%] sm:max-w-[85%] lg:max-w-[60%] rounded-2xl lg:rounded-none overflow-hidden border-2 flex flex-col ${isMe ? 'border-zinc-700 bg-black' : 'border-zinc-800 bg-zinc-900'}`}>
                                <div className="aspect-video bg-black relative border-b-2 border-inherit group">
                                    <img src={msg.product.images?.[0] || msg.product.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                    <div className={`absolute top-3 sm:top-4 right-3 sm:right-4 px-2 sm:px-3 py-1 text-[9px] sm:text-[10px] font-black tracking-widest uppercase border-2 shadow-xl rounded-full lg:rounded-none ${isPaidGift ? 'bg-white text-black border-black' : 'bg-black text-white border-zinc-700'}`}>
                                        {isPaidGift ? 'Transferred' : isPendingGift ? 'Reserved' : isItemShare ? 'Shared' : isItemReq ? 'Requested' : 'Gift'}
                                    </div>
                                </div>
                                <div className="p-4 sm:p-6 flex flex-col">
                                    <p className="text-white font-black uppercase tracking-widest text-base sm:text-lg leading-tight mb-2 truncate">{msg.product.name}</p>
                                    <p className="text-zinc-500 font-mono text-xs sm:text-sm mb-4 sm:mb-6">ZAR {msg.product.price}</p>
                                    
                                    {isPendingGift ? (
                                        <div className="border-t-2 border-dashed border-zinc-700 pt-4 flex justify-between items-center"><p className="text-white text-[9px] sm:text-[10px] font-black uppercase tracking-[0.3em]">In Cart</p><p className="text-zinc-500 text-[9px] sm:text-[10px] font-mono">Exp: 24H</p></div>
                                    ) : isPaidGift ? (
                                        <div className="border-t-2 border-zinc-800 pt-4 sm:pt-6">
                                            <div className="text-white text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.2em] mb-4 border-l-4 border-white pl-3 sm:pl-4 leading-relaxed">{msg.text}</div>
                                            {showClaimButton && <button onClick={() => { setClaimingGiftId(msg.orderId); setClaimingMessageId(msg.id); }} className="w-full rounded-full lg:rounded-none border-2 border-white bg-white text-black font-black uppercase tracking-[0.2em] text-[9px] sm:text-[10px] py-3 sm:py-4 hover:bg-zinc-200 transition-colors shadow-2xl">Initialize Delivery</button>}
                                        </div>
                                    ) : (isItemShare || isItemReq) ? (
                                        <button onClick={() => { 
                                            onClose(); 
                                            if (onSelectProduct) {
                                                onSelectProduct(msg.product); 
                                            } else if (msg.product?.id) {
                                                navigate(`/product/${msg.product.id}`); 
                                            }
                                        }} className={`w-full py-3 sm:py-4 rounded-full lg:rounded-none border-2 font-black uppercase tracking-[0.2em] text-[9px] sm:text-[10px] transition-colors ${isMe ? 'bg-white text-black border-white hover:bg-zinc-200' : 'bg-black text-white border-zinc-700 hover:border-white'}`}>
                                            View Product
                                        </button>
                                    ) : <p className="text-white font-mono text-[9px] sm:text-[10px] uppercase tracking-widest border-l-4 border-zinc-700 pl-3 sm:pl-4 mt-2">"{msg.text}"</p>}
                                </div>
                            </div>
                        ) : isCashReq ? (
                            <div className={`max-w-[90%] sm:max-w-[85%] lg:max-w-[60%] p-6 sm:p-8 rounded-2xl lg:rounded-none border-2 flex flex-col ${isMe ? 'border-zinc-700 bg-black' : 'border-zinc-800 bg-zinc-900'}`}>
                                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 text-zinc-500 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] border-b border-zinc-800 pb-3 sm:pb-4"><Banknote size={14}/> System: Transfer Request</div>
                                <p className="text-4xl sm:text-5xl font-black text-white italic tracking-tighter truncate">{msg.amount} CRD</p>
                            </div>
                        ) : isCashSent ? (
                            <div className={`max-w-[90%] sm:max-w-[85%] lg:max-w-[60%] p-6 sm:p-8 rounded-2xl lg:rounded-none border-2 border-white bg-white text-black flex flex-col shadow-lg lg:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.2)]`}>
                                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 text-black/70 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] border-b border-black/20 pb-3 sm:pb-4"><CheckCircle size={14}/> Transfer Complete</div>
                                <p className="text-4xl sm:text-5xl font-black italic tracking-tighter truncate">{msg.amount} CRD</p>
                            </div>
                        ) : isImageShare ? (
                            <div className={`max-w-[85%] rounded-2xl lg:rounded-none border-2 p-1.5 sm:p-2 relative group ${isMe ? 'bg-black border-zinc-700 rounded-tr-none lg:rounded-none' : 'bg-zinc-900 border-zinc-800 rounded-tl-none lg:rounded-none'}`}>
                                <img src={msg.imageUrl} className="w-56 sm:w-64 h-auto object-cover rounded-xl lg:rounded-none bg-black" alt="Sent Attachment" />
                                
                                <button 
                                    onClick={() => handleDownloadImage(msg.imageUrl)}
                                    className="absolute top-3 sm:top-4 right-3 sm:right-4 p-2 bg-black/50 hover:bg-white hover:text-black text-white backdrop-blur-md rounded-full lg:rounded-none opacity-0 group-hover:opacity-100 transition-all border border-white/20 shadow-xl"
                                    title="Save Image"
                                >
                                    <Download size={14} />
                                </button>

                                {msg.text && <p className="text-white text-xs sm:text-sm font-mono mt-3 mb-2 px-2">{msg.text}</p>}
                            </div>
                        ) : (
                            <div className={`max-w-[90%] sm:max-w-[80%] px-4 sm:px-5 py-3 sm:py-4 text-xs sm:text-sm font-mono leading-relaxed rounded-2xl lg:rounded-none border-2 ${isMe ? 'bg-white text-black border-white shadow-lg lg:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.15)] rounded-tr-none lg:rounded-none' : 'border-zinc-800 bg-black text-zinc-300 rounded-tl-none lg:rounded-none'}`}>
                                {msg.text}
                            </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={bottomRef} className="h-4" />
                </div>

                {/* Input Area */}
                <div className="p-4 sm:p-6 lg:p-8 bg-black border-t border-zinc-800 shrink-0">
                  {/* Image Preview Container */}
                  {stickerBase64 && (
                      <div className="relative w-20 h-20 sm:w-24 sm:h-24 mb-4 ml-2">
                          <img src={stickerBase64} className="w-full h-full object-cover border-2 border-white rounded-xl lg:rounded-none" />
                          <button onClick={() => setStickerBase64(null)} className="absolute -top-2 -right-2 bg-black text-white rounded-full lg:rounded-none p-1 border border-zinc-800"><X size={12}/></button>
                      </div>
                  )}

                  <div className="flex gap-2 sm:gap-4 mb-4 sm:mb-6">
                    <button onClick={() => setGiftMode('send')} className="flex-1 rounded-xl sm:rounded-2xl lg:rounded-none border-2 border-zinc-800 bg-zinc-950 text-white text-[9px] sm:text-[10px] font-black uppercase tracking-widest py-3 sm:py-4 flex items-center justify-center gap-2 hover:border-white transition-colors group"><Gift size={14} className="text-zinc-500 group-hover:text-white" /> Gift Item</button>
                    <button onClick={() => setGiftMode('request')} className="flex-1 rounded-xl sm:rounded-2xl lg:rounded-none border-2 border-zinc-800 bg-zinc-950 text-white text-[9px] sm:text-[10px] font-black uppercase tracking-widest py-3 sm:py-4 flex items-center justify-center gap-2 hover:border-white transition-colors group"><ShoppingBag size={14} className="text-zinc-500 group-hover:text-white" /> Request Item</button>
                  </div>
                  
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2 sm:gap-4 bg-black border-2 border-zinc-800 focus-within:border-white transition-colors p-1.5 sm:p-2 rounded-full lg:rounded-none">
                    <input type="file" accept="image/*" className="hidden" ref={fileInputRef} onChange={handleImageSelect} />
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2 sm:p-3 shrink-0 text-zinc-500 hover:text-white transition-colors"><ImageIcon size={18} strokeWidth={2}/></button>
                    <input value={inputText} onChange={(e) => setInputText(e.target.value)} className="flex-1 bg-transparent w-full min-w-0 text-white font-mono text-[10px] sm:text-sm outline-none placeholder:text-zinc-700" placeholder="Type message..." />
                    <button type="submit" disabled={!inputText.trim() && !stickerBase64} className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shrink-0 bg-white text-black rounded-full lg:rounded-none hover:bg-zinc-200 transition-colors disabled:opacity-50"><Send size={16} strokeWidth={2.5}/></button>
                  </form>
                </div>
            </div>
        );
  })();

  const EmptyDesktopUI = (
      <div className="flex flex-col h-full bg-zinc-950 items-center justify-center relative w-full opacity-30 select-none">
          <MessageSquare size={100} strokeWidth={1} className="text-white mb-8" />
          <h2 className="text-6xl font-black text-white italic uppercase tracking-tighter mb-4">Messages Offline</h2>
          <p className="font-mono uppercase tracking-[0.4em] text-xs text-zinc-500">Awaiting Signal</p>
      </div>
  );

  // 🟢 REPORT MODAL UI
  const ReportModalUI = showReportModal && (
      <div className="fixed inset-0 z-[1000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-zinc-950 border border-zinc-800 rounded-[2rem] lg:rounded-none w-full max-w-sm flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95">
              <div className="p-6 border-b border-zinc-900 flex justify-between items-center bg-black">
                  <h3 className="text-white font-black uppercase tracking-widest text-sm flex items-center gap-2">
                      <Flag size={16} className="text-red-500" /> Report Comms
                  </h3>
                  <button onClick={() => setShowReportModal(false)} className="text-zinc-500 hover:text-white transition-colors"><X size={18}/></button>
              </div>
              <div className="p-8 text-center flex flex-col items-center">
                  {reportStatus === 'idle' && (
                      <>
                          <AlertCircle size={48} className="text-red-500 mb-6" />
                          <h4 className="text-xl font-black text-white uppercase italic tracking-tighter mb-2">Flag Conversation?</h4>
                          <p className="text-zinc-400 font-mono text-xs uppercase tracking-widest leading-relaxed mb-8">This will securely transmit the chat logs to moderation for review.</p>
                          <div className="flex w-full gap-3">
                              <button onClick={() => setShowReportModal(false)} className="flex-1 py-4 border-2 border-zinc-800 text-white font-black text-[10px] uppercase tracking-[0.2em] hover:bg-zinc-900 transition-colors rounded-full lg:rounded-none">Cancel</button>
                              <button onClick={() => {
                                  setReportStatus('reporting');
                                  setTimeout(() => setReportStatus('success'), 1500);
                              }} className="flex-1 py-4 border-2 border-red-500 bg-red-500/10 text-red-500 font-black text-[10px] uppercase tracking-[0.2em] hover:bg-red-500 hover:text-white transition-colors rounded-full lg:rounded-none">Report</button>
                          </div>
                      </>
                  )}
                  {reportStatus === 'reporting' && (
                      <div className="py-10 flex flex-col items-center">
                          <Loader2 size={48} className="text-red-500 animate-spin mb-6" />
                          <p className="text-red-500 font-mono text-xs uppercase tracking-[0.3em] animate-pulse">Transmitting...</p>
                      </div>
                  )}
                  {reportStatus === 'success' && (
                      <div className="py-8 flex flex-col items-center animate-in zoom-in">
                          <ShieldCheck size={64} className="text-green-500 mb-6" />
                          <h4 className="text-2xl font-black text-white uppercase italic tracking-tighter mb-2">Report Logged</h4>
                          <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-[0.2em] mb-8">Moderation team notified.</p>
                          <button onClick={() => setShowReportModal(false)} className="w-full py-4 border border-white bg-white text-black font-black text-[10px] uppercase tracking-[0.2em] hover:bg-zinc-200 transition-colors rounded-full lg:rounded-none">Acknowledge</button>
                      </div>
                  )}
              </div>
          </div>
      </div>
  );

  const renderMobileContent = () => {
      if (claimingGiftId) return ClaimUI;
      if (confirmingProduct || (giftType === 'cash' && giftAmount && !giftMode)) return PaymentUI;
      if (giftMode && activeChat && giftMode !== 'share') return ShopUI;
      if (activeChat) return ChatUI;
      return InboxUI;
  };

  const renderDesktopRightContent = () => {
      if (!activeChat) return EmptyDesktopUI;
      if (claimingGiftId) return ClaimUI;
      if (confirmingProduct || (giftType === 'cash' && giftAmount && !giftMode)) return PaymentUI;
      if (giftMode && activeChat && giftMode !== 'share') return ShopUI;
      return ChatUI;
  };

  return (
      <div className={`fixed inset-0 z-[300] bg-black/90 backdrop-blur-md flex flex-col lg:flex-row items-end lg:items-center justify-center animate-in fade-in duration-300 overscroll-none ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
          {ReportModalUI}
          
          {/* 🟢 DESIGN PREVIEW MODAL */}
          <DesignPreviewModal 
            isOpen={showDesignPreview}
            onClose={() => setShowDesignPreview(false)}
            design={selectedDesign}
          />
          
          <div className={`lg:hidden w-full h-[95vh] bg-black flex flex-col transform transition-transform duration-300 ease-out rounded-t-[2.5rem] overflow-hidden ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
             {renderMobileContent()}
          </div>

          <div className={`hidden lg:flex w-full h-full bg-black overflow-hidden transition-all duration-300 ease-out ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.98]'}`}>
             <div className="absolute top-8 right-8 z-50 flex items-center gap-4">
                 {activeChat && (
                     <button onClick={() => handleDeleteChat(activeChat.id)} className="p-4 bg-zinc-900 text-red-500 hover:bg-red-500 hover:text-white border border-zinc-800 transition-colors rounded-none shadow-2xl" title="Delete Chat">
                         <Trash2 size={24} strokeWidth={2}/>
                     </button>
                 )}
                 <button onClick={onClose} className="p-4 bg-white text-black hover:bg-zinc-200 transition-colors rounded-none shadow-2xl" title="Close Messages">
                     <X size={24} strokeWidth={3}/>
                 </button>
             </div>

             <div className="w-[400px] xl:w-[450px] border-r-2 border-zinc-800 flex flex-col h-full bg-black shrink-0 relative z-20">
                {InboxUI}
             </div>
             <div className="flex-1 flex flex-col h-full bg-zinc-950 relative overflow-hidden z-10">
                {renderDesktopRightContent()}
             </div>
          </div>
      </div>
  );
};

export default MessagesPanel;